import { ChevronDown, XClose } from "@untitled-ui/icons-react";
import {
  Fragment,
  type Key,
  type ReactNode,
  type UIEvent,
  useContext,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useEvent } from "@/hooks";
import { cn } from "@/lib/utils";

import LoadingDots from "../../../loadings/LoadingDots";
import Description from "../../Description";
import { FieldContext } from "../../FieldContext";
import { ComboboxContext, ComboboxSelectedContext } from "./context";

export type ComboboxProps<
  TType = string,
  TMultiple extends boolean = false,
  TRemovable extends boolean = false,
> = {
  options?: TType[];
  multiple?: TMultiple;
  removable?: TRemovable;

  error?: string;
  description?: ReactNode;
  className?: string;

  disabled?: boolean;

  value?: TMultiple extends true ? TType[] : TType | null;
  isLoading?: boolean;
  /** Called when the options list is scrolled near the bottom. */
  onLoadMore?: () => void;
  isLoadingMore?: boolean;
  onChange?: (
    value: TMultiple extends true
      ? TType[]
      : TRemovable extends true
        ? TType | null
        : TType,
  ) => void;

  renderItem: (props: {
    item: NonNullable<TType>;
    index: number;
    selected: boolean;
    selectedOption: boolean;
  }) => ReactNode;
  getItemKey?: (item: TType) => Key;

  startIcon?: ReactNode;
  hideChevron?: boolean;
  truncate?: boolean;

  children?: ReactNode;
  placeholder?: string;
  by?: ByComparator<NoInfer<TType>>;
};

export default function Combobox<
  TType = string,
  TMultiple extends boolean = false,
  TRemovable extends boolean = false,
>({
  options,
  multiple,
  removable,

  error,
  description,
  className,
  disabled,

  value,
  onChange,

  renderItem,

  startIcon,
  hideChevron = false,
  truncate,
  children,
  isLoading,
  onLoadMore,
  isLoadingMore,

  placeholder,

  by,
  getItemKey,
}: ComboboxProps<TType, TMultiple, TRemovable>) {
  const { t } = useTranslation("core.shared");

  const [open, setOpen] = useState(false);

  const fieldContext = useContext(FieldContext);

  const compare = createCompare(by);
  const defaultPlaceholder = t("Select an Option");

  const checkIfSelected = (item: TType) => {
    if (!value) return false;

    if (multiple && Array.isArray(value)) {
      return value.some((v) => compare(v, item));
    }

    return compare(value as TType, item);
  };

  const onSelect = useEvent((item: TType) => {
    if (multiple) {
      onChange?.([...((value as TType[]) || []), item] as any);
    } else {
      onChange?.(item as any);
      setOpen(false);
    }
  });

  const onRemove = useEvent((item: TType) => {
    if (multiple && Array.isArray(value)) {
      onChange?.(value.filter((v) => !compare(v, item)) as any);
    } else {
      onChange?.(null as any);
    }
  });

  const handleListScroll = useEvent((event: UIEvent<HTMLDivElement>) => {
    if (!onLoadMore || isLoadingMore) return;

    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < 48) {
      onLoadMore();
    }
  });

  const hasSelectedValue =
    value && (!multiple || (value as TType[]).length !== 0);

  return (
    <ComboboxContext.Provider
      value={{
        multiple: multiple || false,
        removable: removable || false,
        onSelect,
        onRemove,
        truncate,
      }}
    >
      <div className={fieldContext.grid ? "sm:col-span-2" : undefined}>
        <Popover
          open={open}
          onOpenChange={(value) => {
            if (disabled) return;
            setOpen(value);
          }}
        >
          <PopoverTrigger asChild>
            <button
              disabled={disabled}
              color="secondary-gray"
              role="combobox"
              aria-expanded={open}
              className={cn(
                "flex w-full cursor-pointer items-center gap-2 rounded-lg border bg-white px-3.5 py-[9px] text-base text-gray-900 shadow-xs select-none",
                "focus:outline-hidden data-[state=open]:ring-4",
                error
                  ? "border-error-300 focus:ring-error-100 data-[state=open]:ring-error-100"
                  : "focus:border-primary-300 data-[state=open]:border-primary-300 focus:ring-primary-100 data-[state=open]:ring-primary-100 border-gray-300",
                "disabled:cursor-default disabled:bg-gray-50 disabled:text-gray-500",
                className,
              )}
            >
              {startIcon && (
                <div className="flex items-center">{startIcon}</div>
              )}
              <div
                className={cn(
                  multiple && "me-1.5 flex-wrap gap-1.5",
                  truncate && "min-w-0",
                  "flex flex-1",
                )}
              >
                <ComboboxSelectedContext.Provider value={true}>
                  {hasSelectedValue ? (
                    multiple ? (
                      (value as NonNullable<TType>[]).map((item, index) =>
                        renderItem({
                          item,
                          index,
                          selected: true,
                          selectedOption: true,
                        }),
                      )
                    ) : (
                      renderItem({
                        item: value as NonNullable<TType>,
                        index: 0,
                        selected: true,
                        selectedOption: true,
                      })
                    )
                  ) : (
                    <span className="text-gray-500">
                      {placeholder || defaultPlaceholder}
                    </span>
                  )}
                </ComboboxSelectedContext.Provider>
              </div>
              <div className="flex items-center">
                {isLoading ? (
                  <LoadingDots className="ms-auto" />
                ) : !multiple && hasSelectedValue && removable ? (
                  <XClose
                    className="ms-auto size-5 text-gray-500"
                    onClick={(e) => {
                      e.stopPropagation();
                      onChange?.(null as any);
                    }}
                  />
                ) : !hideChevron ? (
                  <ChevronDown className="ms-auto size-5 text-gray-500" />
                ) : null}
              </div>
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-max max-w-sm min-w-(--radix-popover-trigger-width) p-0">
            <Command value="12124124" disablePointerSelection>
              {children && <div className="pt-1">{children}</div>}
              <CommandList onScroll={onLoadMore ? handleListScroll : undefined}>
                <CommandEmpty>
                  {isLoading ? (
                    <LoadingDots className="mx-auto" />
                  ) : (
                    t("No data found")
                  )}
                </CommandEmpty>
                <CommandGroup>
                  {options?.map((item, index) => (
                    <Fragment key={getItemKey?.(item) || index}>
                      {renderItem({
                        item: item as NonNullable<TType>,
                        index,
                        selected: checkIfSelected(item),
                        selectedOption: false,
                      })}
                    </Fragment>
                  ))}
                </CommandGroup>
                {isLoadingMore && (
                  <div className="flex justify-center py-2">
                    <LoadingDots />
                  </div>
                )}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        {(error || description) && (
          <Description variant={error ? "error" : "hint"} className="mt-1.5">
            {error || description}
          </Description>
        )}
      </div>
    </ComboboxContext.Provider>
  );
}

type ByComparator<T> =
  | (NonNullable<T> extends never ? string : keyof NonNullable<T> & string)
  | ((a: T, z: T) => boolean);

function defaultBy<T>(a: T, z: T) {
  if (
    a !== null &&
    z !== null &&
    typeof a === "object" &&
    typeof z === "object" &&
    "id" in a &&
    "id" in z
  ) {
    return a.id === z.id;
  }

  return a === z;
}

function createCompare<T>(by: ByComparator<T> = defaultBy) {
  return (a: T, z: T) => {
    if (typeof by === "string") {
      const property = by as keyof T;
      return a?.[property] === z?.[property];
    }

    return by(a, z);
  };
}
